package com.isaias.fyattravel.web.pages.contact

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors

@Composable
fun ContactPage() {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var sent by remember { mutableStateOf(false) }
    val whatsappNumber = "256758250625"
    val locale = WebLocaleStore.locale.value
    fun t(key: WebStringKey) = WebStrings.get(locale, key)
    fun tf(key: WebStringKey, vararg args: String) = WebStrings.format(locale, key, *args)
    fun sendWhatsApp() {
        sent = false
        error = null
        if (name.isBlank() || message.isBlank()) {
            error = t(WebStringKey.ERROR_NAME_MESSAGE_REQUIRED)
            return
        }
        val emailValue = if (email.isBlank()) t(WebStringKey.NOT_PROVIDED) else email
        val formattedMessage = tf(WebStringKey.CONTACT_MESSAGE_TEMPLATE, name, emailValue, message)
        val encoded = js("encodeURIComponent")(formattedMessage) as String
        window.open("https://wa.me/$whatsappNumber?text=$encoded", "_blank")
        sent = true
    }
    Div({
        style {
            padding(2.em, 1.5.em)
            backgroundColor(ThemeColors.surface)
            property("@media (max-width: 640px)", "{ padding: 1.2em 0.9em; }")
        }
    }) {
        Div({
            style {
                maxWidth(1100.px)
                property("margin", "0 auto")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(0.75.em)
                    marginBottom(1.25.em)
                }
            }) {
                Img(src = "icon_fyat.jpg", attrs = {
                    style {
                        width(44.px)
                        height(44.px)
                        borderRadius(10.px)
                        property("object-fit", "contain")
                    }
                })
                Div {
                    Div({
                        style {
                            fontSize(1.25.em)
                            fontWeight("800")
                            color(ThemeColors.textPrimary)
                            property("@media (max-width: 640px)", "{ font-size: 1.05em; }")
                        }
                    }) { Text(WebStrings.t(WebStringKey.FYAT_TRAVEL_TOUR)) }
                    Div({
                        style {
                            fontSize(0.9.em)
                            color(ThemeColors.textMuted)
                            property("@media (max-width: 640px)", "{ font-size: 0.85em; }")
                        }
                    }) { Text(WebStrings.t(WebStringKey.CONTACT_SUBTITLE)) }
                }
            }
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1.1fr 1fr")
                    gap(1.5.em)
                    property("@media (max-width: 900px)", "{ grid-template-columns: 1fr; }")
                }
            }) {
                Div({
                    style {
                        backgroundColor(ThemeColors.surface)
                        borderRadius(16.px)
                        padding(1.5.em)
                        property("box-shadow", "0 8px 24px ${ThemeColors.shadow}")
                    }
                }) {
                    H3({ style { marginBottom(0.75.em); color(ThemeColors.textPrimary) } }) { Text(WebStrings.t(WebStringKey.CONTACT_INFORMATION)) }
                    ContactLine("📍", "Kampala, Uganda") { window.open("https://share.google/tbORf5cegPI4ODG3G", "_blank") }
                    ContactLine("✉️", "info@fyattraveltour.com") { window.open("mailto:info@fyattraveltour.com", "_self") }
                    ContactLine("💬", "+256 758 250 625 (WhatsApp)") { window.open("https://wa.me/256758250625", "_blank") }
                    ContactLine("📱", "+256 758 249 530") { window.open("https://wa.me/256758249530", "_blank") }
                    Hr({ style { margin(1.25.em, 0.em) } })
                    H3({ style { marginBottom(0.75.em); color(ThemeColors.textPrimary) } }) { Text(WebStrings.t(WebStringKey.SOCIAL_LABEL)) }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(0.75.em)
                            flexWrap(FlexWrap.Wrap)
                        }
                    }) {
                        SocialChip(WebStrings.t(WebStringKey.INSTAGRAM), "https://instagram.com/fyattravel")
                        SocialChip(WebStrings.t(WebStringKey.WHATSAPP), "https://wa.me/256758250625")
                    }
                }
                Div({
                    style {
                        backgroundColor(ThemeColors.surface)
                        borderRadius(16.px)
                        padding(1.5.em)
                        property("box-shadow", "0 8px 24px ${ThemeColors.shadow}")
                    }
                }) {
                    H3({ style { marginBottom(0.75.em); color(ThemeColors.textPrimary) } }) { Text(WebStrings.t(WebStringKey.SEND_A_COMMENT)) }
                    FormInput(WebStrings.t(WebStringKey.YOUR_NAME), name, WebStrings.t(WebStringKey.FULL_NAME_PLACEHOLDER)) { name = it }
                    FormInput(WebStrings.t(WebStringKey.EMAIL_OPTIONAL), email, WebStrings.t(WebStringKey.EMAIL_PLACEHOLDER)) { email = it }
                    FormTextArea(WebStrings.t(WebStringKey.MESSAGE_COMMENTS), message, WebStrings.t(WebStringKey.MESSAGE_PLACEHOLDER)) { message = it }
                    if (error != null) {
                        Div({ style { color(ThemeColors.error); fontSize(0.85.em); marginBottom(0.75.em) } }) { Text(error ?: "") }
                    }
                    if (sent) {
                        Div({ style { color(ThemeColors.success); fontSize(0.85.em); marginBottom(0.75.em) } }) { Text(WebStrings.t(WebStringKey.WHATSAPP_SENT_HINT)) }
                    }
                    Button(attrs = {
                        style {
                            backgroundColor(ThemeColors.success)
                            color(ThemeColors.textOnDark)
                            border(0.px)
                            padding(0.85.em, 1.25.em)
                            borderRadius(10.px)
                            fontWeight("700")
                            cursor("pointer")
                            width(100.percent)
                        }
                        onClick {
                            it.preventDefault()
                            sendWhatsApp()
                        }
                    }) { Text(WebStrings.t(WebStringKey.SEND_VIA_WHATSAPP)) }
                }
            }
        }
    }
}

@Composable
private fun ContactLine(icon: String, text: String, onClick: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.6.em)
            marginBottom(0.6.em)
            color(ThemeColors.textMuted)
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Span({ style { fontSize(1.em) } }) { Text(icon) }
        Span({ style { fontSize(0.95.em) } }) { Text(text) }
    }
}

@Composable
private fun SocialChip(label: String, url: String) {
    Span({
        style {
            padding(0.4.em, 0.8.em)
            borderRadius(999.px)
            backgroundColor(ThemeColors.primaryMuted)
            color(ThemeColors.primary)
            fontSize(0.85.em)
            fontWeight("600")
            cursor("pointer")
        }
        onClick { window.open(url, "_blank") }
    }) { Text(label) }
}

@Composable
private fun FormInput(label: String, value: String, placeholder: String, onChange: (String) -> Unit) {
    Div({ style { marginBottom(0.9.em) } }) {
        Label(attrs = {
            style {
                display(DisplayStyle.Block)
                marginBottom(0.35.em)
                fontSize(0.85.em)
                color(ThemeColors.textMuted)
            }
        }) { Text(label) }
        Input(type = InputType.Text, attrs = {
            value(value)
            attr("placeholder", placeholder)
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, ThemeColors.divider)
                fontSize(0.95.em)
            }
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun FormTextArea(label: String, value: String, placeholder: String, onChange: (String) -> Unit) {
    Div({ style { marginBottom(0.9.em) } }) {
        Label(attrs = {
            style {
                display(DisplayStyle.Block)
                marginBottom(0.35.em)
                fontSize(0.85.em)
                color(ThemeColors.textMuted)
            }
        }) { Text(label) }
        TextArea(attrs = {
            value(value)
            attr("placeholder", placeholder)
            style {
                width(100.percent)
                minHeight(140.px)
                padding(0.75.em)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, ThemeColors.divider)
                fontSize(0.95.em)
                property("resize", "vertical")
            }
            onInput { onChange(it.value) }
        })
    }
}
